import * as React from 'react';
import {CheckIcon, ChevronDown, XIcon, Loader2} from 'lucide-react';
import {cn} from '@common-web/lib/utils';
import {Popover, PopoverContent, PopoverTrigger} from '@common-web/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@common-web/components/ui/command';

const triggerClasses =
  'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-ring data-[state=open]:ring-1 data-[state=open]:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

const contentClasses = 'w-[var(--radix-popover-trigger-width)] p-0';

type ComboboxProps = {
  id?: string;
  items: {label: string; value: string}[];
  message: string;
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  emptyMessage?: string;
  allowClear?: boolean;
  className?: string;
  /** Custom match scorer (cmdk signature; 0 hides the item). Defaults to cmdk's fuzzy match. */
  filter?: (value: string, search: string) => number;
};

/** Plain case-insensitive "contains" match — for long lists where fuzzy matching over-includes. */
export function containsFilter(value: string, search: string): number {
  return value.toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0;
}

export function Combobox(props: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const selectedItem = props.items.find((item) => item.value === props.value);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <button id={props.id} type='button' className={cn(triggerClasses, props.className)}>
          <span className={cn('truncate', !selectedItem && 'text-muted-foreground')}>
            {selectedItem?.label || props.message}
          </span>
          <div className='flex items-center gap-1 ml-2 shrink-0'>
            {props.allowClear && props.value && (
              <XIcon
                className='h-4 w-4 opacity-50 transition-opacity hover:opacity-100'
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  props.setValue('');
                }}
              />
            )}
            <ChevronDown className='h-4 w-4 opacity-50' />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className={contentClasses}>
        <Command filter={props.filter}>
          <div className='relative'>
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={props.placeholder || 'Search...'}
              className='pr-8 focus:ring-0 focus-visible:ring-0'
            />
            {search && (
              <button
                type='button'
                aria-label='Clear search'
                onClick={() => setSearch('')}
                className='absolute right-3 top-1/2 -translate-y-1/2 opacity-50 transition-opacity hover:opacity-100'
              >
                <XIcon className='h-4 w-4' />
              </button>
            )}
          </div>
          <CommandList>
            <CommandEmpty>{props.emptyMessage || 'No results found.'}</CommandEmpty>
            <CommandGroup>
              {props.items.map((item) => {
                const isSelected = props.value === item.value;
                return (
                  <CommandItem
                    key={item.value}
                    value={item.label}
                    onSelect={() => {
                      props.setValue(item.value === props.value ? '' : item.value);
                      setOpen(false);
                    }}
                  >
                    <CheckIcon
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        isSelected ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className='min-w-0 truncate'>{item.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type AsyncComboboxProps = {
  id?: string;
  value: string;
  selectedLabel?: string;
  setValue: (value: string, label: string) => void;
  onSearch: (search: string) => void;
  items: {label: string; value: string}[];
  isLoading?: boolean;
  message: string;
  placeholder?: string;
  emptyMessage?: string;
  allowClear?: boolean;
  className?: string;
  debounceMs?: number;
};

export function AsyncCombobox(props: AsyncComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout>>();
  const debounceMs = props.debounceMs ?? 300;

  const {onSearch} = props;

  React.useEffect(() => {
    if (open) {
      onSearch('');
    } else {
      setSearch('');
    }
  }, [open, onSearch]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onSearch(value);
    }, debounceMs);
  };

  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button id={props.id} type='button' className={cn(triggerClasses, props.className)}>
          <span className={cn('truncate', !props.selectedLabel && 'text-muted-foreground')}>
            {props.selectedLabel || props.message}
          </span>
          <div className='flex items-center gap-1 ml-2 shrink-0'>
            {props.allowClear && props.value && (
              <XIcon
                className='h-4 w-4 opacity-50 transition-opacity hover:opacity-100'
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  props.setValue('', '');
                }}
              />
            )}
            <ChevronDown className='h-4 w-4 opacity-50' />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' className={contentClasses}>
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={handleSearchChange}
            placeholder={props.placeholder || 'Search...'}
            className='focus:ring-0 focus-visible:ring-0'
          />
          <CommandList>
            {props.isLoading ? (
              <div className='flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                Loading...
              </div>
            ) : (
              <>
                <CommandEmpty>{props.emptyMessage || 'No results found.'}</CommandEmpty>
                <CommandGroup>
                  {props.items.map((item) => {
                    const isSelected = props.value === item.value;
                    return (
                      <CommandItem
                        key={item.value}
                        value={item.value}
                        onSelect={() => {
                          props.setValue(item.value, item.label);
                          setOpen(false);
                        }}
                      >
                        <CheckIcon
                          className={cn(
                            'mr-2 h-4 w-4 shrink-0',
                            isSelected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className='truncate'>{item.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
